import React, { useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, SearchIcon, EyeIcon, UsersIcon, UserIcon } from 'lucide-react';
import { VisibilityProfile, NoteVisibility } from './NotesManagement';
interface VisibilityProfilesManagementProps {
  visibilityProfiles: VisibilityProfile[];
  setVisibilityProfiles: React.Dispatch<React.SetStateAction<VisibilityProfile[]>>;
  locale: string;
}
export function VisibilityProfilesManagement({
  visibilityProfiles,
  setVisibilityProfiles,
  locale
}: VisibilityProfilesManagementProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState<VisibilityProfile | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    visibility: NoteVisibility;
    visibilityDetails: string;
  }>({
    name: '',
    description: '',
    visibility: 'Role-based',
    visibilityDetails: ''
  });
  // Apply search filter
  const filteredProfiles = visibilityProfiles.filter(profile => {
    return profile.name.toLowerCase().includes(searchTerm.toLowerCase()) || profile.description && profile.description.toLowerCase().includes(searchTerm.toLowerCase()) || profile.visibilityDetails && profile.visibilityDetails.toLowerCase().includes(searchTerm.toLowerCase());
  });
  const handleAddProfile = () => {
    setFormData({
      name: '',
      description: '',
      visibility: 'Role-based',
      visibilityDetails: ''
    });
    setEditingProfile(null);
    setShowForm(true);
  };
  const handleEditProfile = (profile: VisibilityProfile) => {
    setFormData({
      name: profile.name,
      description: profile.description,
      visibility: profile.visibility,
      visibilityDetails: profile.visibilityDetails || ''
    });
    setEditingProfile(profile);
    setShowForm(true);
  };
  const handleDeleteProfile = (id: string) => {
    setVisibilityProfiles(visibilityProfiles.filter(profile => profile.id !== id));
  };
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingProfile) {
      // Update existing profile
      setVisibilityProfiles(visibilityProfiles.map(profile => profile.id === editingProfile.id ? {
        ...profile,
        name: formData.name,
        description: formData.description,
        visibility: formData.visibility,
        visibilityDetails: formData.visibilityDetails || undefined,
        updatedAt: new Date().toISOString()
      } : profile));
    } else {
      // Create new profile
      const newProfile: VisibilityProfile = {
        id: `visprofile-${String(visibilityProfiles.length + 1).padStart(3, '0')}`,
        name: formData.name,
        description: formData.description,
        visibility: formData.visibility,
        visibilityDetails: formData.visibilityDetails || undefined,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setVisibilityProfiles([...visibilityProfiles, newProfile]);
    }
    setShowForm(false);
    setEditingProfile(null);
  };
  const handleCancel = () => {
    setShowForm(false);
    setEditingProfile(null);
  };
  // Render visibility badge
  const renderVisibilityBadge = (visibility: NoteVisibility) => {
    switch (visibility) {
      case 'Public':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-19436">
            Public
          </span>;
      case 'Role-based':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-19437">
            Role-based
          </span>;
      case 'User-specific':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-19438">
            User-specific
          </span>;
      case 'Creator-only':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-19439">
            Private
          </span>;
      default:
        return null;
    }
  };
  return <div className="space-y-6" data-id="element-19440">
      {showForm ? <div className="bg-white shadow sm:rounded-lg" data-id="element-19441">
          <div className="px-4 py-5 sm:p-6" data-id="element-19442">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19443">
              {editingProfile ? 'Edit Visibility Profile' : 'Add Visibility Profile'}
            </h3>
            <form onSubmit={handleSubmit} className="mt-5 space-y-6" data-id="element-19444">
              <div data-id="element-19445">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-19446">
                  Name <span className="text-red-500" data-id="element-19447">*</span>
                </label>
                <input type="text" name="name" id="name" value={formData.name} onChange={handleFormChange} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-19448" />
              </div>
              <div data-id="element-19449">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-19450">
                  Description
                </label>
                <textarea name="description" id="description" rows={3} value={formData.description} onChange={handleFormChange} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-19451" />
              </div>
              <div data-id="element-19452">
                <label htmlFor="visibility" className="block text-sm font-medium text-gray-700" data-id="element-19453">
                  Visibility Type <span className="text-red-500" data-id="element-19454">*</span>
                </label>
                <select name="visibility" id="visibility" value={formData.visibility} onChange={handleFormChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-19455">
                  <option value="Public" data-id="element-19456">Public</option>
                  <option value="Role-based" data-id="element-19457">Role-based</option>
                  <option value="User-specific" data-id="element-19458">User-specific</option>
                  <option value="Creator-only" data-id="element-19459">Creator-only (Private)</option>
                </select>
              </div>
              {(formData.visibility === 'Role-based' || formData.visibility === 'User-specific') && <div data-id="element-19460">
                  <label htmlFor="visibilityDetails" className="block text-sm font-medium text-gray-700" data-id="element-19461">
                    {formData.visibility === 'Role-based' ? 'Roles' : 'Users'}{' '}
                    <span className="text-red-500" data-id="element-19462">*</span>
                  </label>
                  <input type="text" name="visibilityDetails" id="visibilityDetails" value={formData.visibilityDetails} onChange={handleFormChange} required placeholder={formData.visibility === 'Role-based' ? 'Enter role names separated by commas' : 'Enter usernames separated by commas'} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-19463" />
                  <p className="mt-1 text-sm text-gray-500" data-id="element-19464">
                    {formData.visibility === 'Role-based' ? 'Example: Finance Manager, Auditor, Procurement Officer' : 'Example: john.smith@example.com, jane.doe@example.com'}
                  </p>
                </div>}
              <div className="flex justify-end space-x-3" data-id="element-19465">
                <button type="button" onClick={handleCancel} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19466">
                  Cancel
                </button>
                <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19467">
                  {editingProfile ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div> : <>
          <div className="bg-white shadow-sm rounded-lg p-4" data-id="element-19468">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-19469">
              <div className="flex-1 max-w-md" data-id="element-19470">
                <div className="relative rounded-md shadow-sm" data-id="element-19471">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-19472">
                    <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-19473" />
                  </div>
                  <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search visibility profiles..." data-id="element-19474" />
                </div>
              </div>
              <button onClick={handleAddProfile} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19475">
                <PlusIcon className="h-4 w-4 mr-2" data-id="element-19476" />
                Add Visibility Profile
              </button>
            </div>
          </div>
          <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-19477">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-19478">
              <thead className="bg-gray-50" data-id="element-19479">
                <tr data-id="element-19480">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-19481">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-19482">
                    Description
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-19483">
                    Visibility
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-19484">
                    Details
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-19485">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-19486">
                {filteredProfiles.length === 0 ? <tr data-id="element-19487">
                    <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-19488">
                      No visibility profiles found. Click "Add Visibility
                      Profile" to create one.
                    </td>
                  </tr> : filteredProfiles.map(profile => <tr key={profile.id} className="hover:bg-gray-50" data-id="element-19489">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-19490">
                        <div className="flex items-center" data-id="element-19491">
                          <EyeIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-19492" />
                          <div className="text-sm font-medium text-gray-900" data-id="element-19493">
                            {profile.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-19494">
                        {profile.description || <span className="text-gray-400 italic" data-id="element-19495">
                            No description
                          </span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-19496">
                        {renderVisibilityBadge(profile.visibility)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-19497">
                        {profile.visibility === 'Public' && <span className="text-sm text-gray-500" data-id="element-19498">
                            Visible to all users
                          </span>}
                        {profile.visibility === 'Role-based' && profile.visibilityDetails && <div className="flex items-center" data-id="element-19499">
                              <UsersIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-19500" />
                              <span data-id="element-19501">{profile.visibilityDetails}</span>
                            </div>}
                        {profile.visibility === 'User-specific' && profile.visibilityDetails && <div className="flex items-center" data-id="element-19502">
                              <UserIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-19503" />
                              <span data-id="element-19504">{profile.visibilityDetails}</span>
                            </div>}
                        {profile.visibility === 'Creator-only' && <span className="text-sm text-gray-500" data-id="element-19505">
                            Visible only to creator
                          </span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-19506">
                        <div className="flex justify-end space-x-2" data-id="element-19507">
                          <button onClick={() => handleEditProfile(profile)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-19508">
                            <EditIcon className="h-4 w-4" data-id="element-19509" />
                          </button>
                          <button onClick={() => handleDeleteProfile(profile.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-19510">
                            <TrashIcon className="h-4 w-4" data-id="element-19511" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
        </>}
    </div>;
}